import api from '@/api'

const initialListPagination = () => ({
  current_page: 1,
  per_page: 25,
  total: 0,
  last_page: 0
})

const initialState = () => ({
  // sequences list
  list: [],
  listLoading: false,
  listError: null,
  listFilters: { station: null, air_date: null, status: null },
  listPagination: initialListPagination(),

  // current sequence
  currentSequence: null,
  currentSequenceLoading: false,
  currentSequenceSegmentsItemsLoading: false,
  currentSequenceTargetsLoading: false,
  currentSequenceError: null,
  currentSequenceId: null,

  // platforms
  platforms: [],
  platformsLoading: false,

  // formats
  formats: [],
  formatsLoading: false,

  // readiness - single
  readiness: {
    sequence: null,
    targets: [],
    segments: [],
    items: []
  },
  readinessLoading: false,

  // readiness - list
  listReadiness: [],
  listReadinessLoading: false,

  // loading flags
  saving: false,
  reordering: false,
  exporting: false,
  locking: false,
  recalculating: false,

  // reorder
  dirtySegments: {},

  // grid
  gridRefreshKey: 0,

  // view/edit modes
  metadataMode: 'view',
  segmentMode: 'view',
  itemMode: 'view',
  targetMode: 'view',

  // active editors
  sequenceActiveEditors: [],

  // unsaved changes
  sequenceUnsavedChanges: false,
  sequenceDirtyContexts: {
    metadata: false,
    segment: false,
    item: false,
    target: false
  }
})

const findIndexById = (items, id) => (items || []).findIndex((i) => i?.id === id)

const upsert = (items, item) => {
  const idx = findIndexById(items, item?.id)
  if (idx >= 0) items.splice(idx, 1, { ...items[idx], ...item })
  else items.push(item)
}

const removeById = (items, id) => {
  const idx = findIndexById(items, id)
  if (idx >= 0) items.splice(idx, 1)
}

const reorderByIds = (items, orderedIds) => {
  if (!Array.isArray(items) || !Array.isArray(orderedIds)) return items
  const map = new Map(items.map((x) => [x.id, x]))
  const ordered = orderedIds.map((id) => map.get(id)).filter(Boolean)
  const leftover = items.filter((x) => !orderedIds.includes(x.id))
  return [...ordered, ...leftover]
}

export default {
  state: initialState(),

  mutations: {
    // sequences list
    SET_SEQUENCE_LIST(state, list) {
      state.list = list || []
    },
    SET_SEQUENCE_LIST_LOADING(state, value) {
      state.listLoading = !!value
    },
    SET_SEQUENCE_LIST_ERROR(state, err) {
      state.listError = err || null
    },
    SET_SEQUENCE_LIST_FILTERS(state, filters) {
      state.listFilters = { ...state.listFilters, ...(filters || {}) }
    },
    SET_SEQUENCE_LIST_PAGINATION(state, pagination) {
      state.listPagination = { ...state.listPagination, ...(pagination || {}) }
    },

    // current sequence
    SET_CURRENT_SEQUENCE(state, sequence) {
      state.currentSequence = sequence || null
      state.currentSequenceId = sequence?.id || null
    },
    SET_CURRENT_SEQUENCE_LOADING(state, value) {
      state.currentSequenceLoading = !!value
    },
    SET_CURRENT_SEQUENCE_SEGMENTS_ITEMS_LOADING(state, value) {
      state.currentSequenceSegmentsItemsLoading = !!value
    },
    SET_CURRENT_SEQUENCE_TARGETS_LOADING(state, value) {
      state.currentSequenceTargetsLoading = !!value
    },
    SET_CURRENT_SEQUENCE_ERROR(state, err) {
      state.currentSequenceError = err || null
    },
    PATCH_CURRENT_SEQUENCE(state, partial) {
      if (!state.currentSequence || !partial) return
      state.currentSequence = { ...state.currentSequence, ...partial }
    },

    // items
    SET_SEQUENCE_ITEMS(state, items) {
      if (!state.currentSequence) return
      state.currentSequence.items = items || []
    },
    ADD_SEQUENCE_ITEM(state, item) {
      if (!state.currentSequence) return
      if (!state.currentSequence.items) state.currentSequence.items = []
      state.currentSequence.items.push(item)
    },
    UPDATE_SEQUENCE_ITEM(state, item) {
      if (!state.currentSequence?.items) return
      upsert(state.currentSequence.items, item)
    },
    REMOVE_SEQUENCE_ITEM(state, id) {
      if (!state.currentSequence) return

      const items = state.currentSequence.items
      if (!Array.isArray(items)) return

      const item = items.find((i) => i.id === id)
      if (!item) return

      const segmentId = item.segment_id

      // remove from global list
      const index = items.findIndex((i) => i.id === id)
      if (index !== -1) items.splice(index, 1)

      // remove from segment only if it exists
      const segment = state.currentSequence.segments?.find((s) => s.id === segmentId)

      if (segment?.items) {
        const segIndex = segment.items.findIndex((i) => i.id === id)
        if (segIndex !== -1) segment.items.splice(segIndex, 1)
      }
    },
    REORDER_SEQUENCE_ITEMS(state, orderedIds) {
      if (!state.currentSequence?.items || !Array.isArray(orderedIds)) return
      const items = state.currentSequence.items
      const idToItem = new Map(items.map((x) => [x.id, x]))
      // Reordered items, with sort_order bumped 1-based to match the backend
      const reordered = orderedIds
        .map((id, index) => {
          const item = idToItem.get(id)
          if (!item) return null
          return { ...item, sort_order: index + 1 }
        })
        .filter(Boolean)
      // Items not in orderedIds — append at end with continuing 1-based sort_order
      const remainder = items.filter((x) => !orderedIds.includes(x.id)).map((x, index) => ({ ...x, sort_order: reordered.length + index + 1 }))
      state.currentSequence.items = [...reordered, ...remainder]
    },
    REORDER_SEQUENCE_SEGMENT_ITEMS(state, { segmentId, orderedIds }) {
      if (!state.currentSequence?.items || !Array.isArray(orderedIds)) return

      const items = state.currentSequence.items
      const idToItem = new Map(items.map((x) => [x.id, x]))

      // 1. Build reordered segment items (from orderedIds)
      const reorderedInSegment = orderedIds
        .map((id, index) => {
          const item = idToItem.get(id)
          if (!item || item.segment_id !== segmentId) return null

          return {
            ...item,
            sort_order_in_segment: index + 1
          }
        })
        .filter(Boolean)

      // 2. Append remaining segment items (not in orderedIds)
      const remainderInSegment = items
        .filter((x) => x.segment_id === segmentId && !orderedIds.includes(x.id))
        .map((x, index) => ({
          ...x,
          sort_order_in_segment: reorderedInSegment.length + index + 1
        }))

      const segmentQueue = [...reorderedInSegment, ...remainderInSegment]

      // 3. Update flat items list
      const newItems = items.map((x) => (x.segment_id === segmentId ? segmentQueue.shift() : x))

      state.currentSequence.items = newItems

      // 4. 🔥 ALSO UPDATE SEGMENT NESTED ITEMS (important for UI consistency)
      if (Array.isArray(state.currentSequence.segments)) {
        const segment = state.currentSequence.segments.find((s) => s.id === segmentId)

        if (segment) {
          // rebuild segment.items from authoritative updated global items
          segment.items = newItems.filter((x) => x.segment_id === segmentId).sort((a, b) => (a.sort_order_in_segment || 0) - (b.sort_order_in_segment || 0))
          if (!state.currentSequence?.segments) return
          upsert(state.currentSequence.segments, segment)
        }
      }
    },

    // segments
    SET_SEQUENCE_SEGMENTS(state, segments) {
      if (!state.currentSequence) return
      state.currentSequence.segments = segments || []
    },
    ADD_SEQUENCE_SEGMENT(state, segment) {
      if (!state.currentSequence) return
      if (!state.currentSequence.segments) state.currentSequence.segments = []
      state.currentSequence.segments.push(segment)
    },
    UPDATE_SEQUENCE_SEGMENT(state, segment) {
      if (!state.currentSequence?.segments) return
      upsert(state.currentSequence.segments, segment)
    },
    REMOVE_SEQUENCE_SEGMENT(state, id) {
      if (!state.currentSequence?.segments) return
      removeById(state.currentSequence.segments, id)
    },
    REORDER_SEQUENCE_SEGMENTS(state, orderedIds) {
      if (!state.currentSequence?.segments || !Array.isArray(orderedIds)) return
      const segments = state.currentSequence.segments
      const idToSegment = new Map(segments.map((x) => [x.id, x]))
      // Reordered segments, with sort_order bumped 1-based to match the backend
      const reordered = orderedIds
        .map((id, index) => {
          const segment = idToSegment.get(id)
          if (!segment) return null
          return { ...segment, sort_order: index + 1 }
        })
        .filter(Boolean)
      // Segments not in orderedIds — append at end with continuing 1-based sort_order
      const remainder = segments.filter((x) => !orderedIds.includes(x.id)).map((x, index) => ({ ...x, sort_order: reordered.length + index + 1 }))
      state.currentSequence.segments = [...reordered, ...remainder]
    },

    // distribution targets
    SET_TARGETS(state, targets) {
      if (!state.currentSequence) return
      state.currentSequence.targets = targets || []
    },
    ADD_TARGET(state, target) {
      if (!state.currentSequence) return
      if (!state.currentSequence.targets) state.currentSequence.targets = []
      state.currentSequence.targets.push(target)
    },
    UPDATE_TARGET(state, target) {
      if (!state.currentSequence?.targets) return
      upsert(state.currentSequence.targets, target)
    },
    REMOVE_TARGET(state, id) {
      if (!state.currentSequence?.targets) return
      removeById(state.currentSequence.targets, id)
    },
    SET_TARGET_EXPORT_STATUS(state, payload) {
      const target = state.currentSequence?.distribution_targets?.find((t) => t.id === payload.target_id)
      if (!target) {
        return
      }
      target.status = payload.status
      if (payload.last_exported_at) {
        target.last_exported_at = payload.last_exported_at
      }
      if (payload.error_message !== undefined) {
        target.error_message = payload.error_message
      }
    },
    ADD_EXPORT_JOB(state, job) {
      if (!job?.id) {
        return
      }
      const exists = state.currentSequence.export_jobs?.some((x) => x.id === job.id)
      if (exists) {
        return
      }
      if (!state.currentSequence?.export_jobs) {
        state.currentSequence.export_jobs = []
      }
      state.currentSequence.export_jobs.unshift(job)
    },
    UPDATE_EXPORT_JOB(state, job) {
      const index = state.currentSequence.export_jobs?.findIndex((x) => x.id === job.id)
      if (index === -1) {
        state.currentSequence.export_jobs.unshift(job)
        return
      }
      state.currentSequence.export_jobs.splice(index, 1, {
        ...state.currentSequence.export_jobs[index],
        ...job
      })
    },

    // platforms
    SET_SEQUENCE_PLATFORMS(state, platforms) {
      state.platforms = platforms || []
    },
    SET_SEQUENCE_PLATFORMS_LOADING(state, value) {
      state.platformsLoading = !!value
    },

    // formats
    SET_FORMATS(state, formats) {
      state.formats = formats || []
    },
    SET_FORMATS_LOADING(state, value) {
      state.formatsLoading = !!value
    },

    // readiness
    SET_READINESS(state, readiness) {
      state.readiness = {
        sequence: readiness?.sequence || null,
        targets: readiness?.targets || [],
        segments: readiness?.segments || [],
        items: readiness?.items || []
      }
    },
    SET_READINESS_LOADING(state, value) {
      state.readinessLoading = !!value
    },
    SET_LIST_READINESS(state, list) {
      state.listReadiness = Array.isArray(list) ? list : []
    },
    SET_LIST_READINESS_LOADING(state, value) {
      state.listReadinessLoading = !!value
    },

    // loading flags
    SET_SAVING(state, value) {
      state.saving = !!value
    },
    SET_REORDERING(state, value) {
      state.reordering = !!value
    },
    SET_EXPORTING(state, value) {
      state.exporting = !!value
    },
    SET_LOCKING(state, value) {
      state.locking = !!value
    },
    SET_RECALCULATING(state, value) {
      state.recalculating = !!value
    },

    // reorder
    SET_DIRTY_SEGMENT(state, { segmentId, originalIds, orderedIds }) {
      state.dirtySegments = {
        ...state.dirtySegments,
        [String(segmentId)]: {
          originalIds: [...originalIds],
          orderedIds: [...orderedIds],
          dirty: true
        }
      }
    },
    CLEAR_DIRTY_SEGMENT(state, segmentId) {
      const dirty = { ...state.dirtySegments }

      delete dirty[String(segmentId)]

      state.dirtySegments = dirty
    },
    CLEAR_ALL_DIRTY_SEGMENTS(state) {
      state.dirtySegments = {}
    },

    // grid
    INCREMENT_GRID_REFRESH_KEY(state) {
      state.gridRefreshKey++
    },

    // view/edit modes
    SET_METADATA_MODE(state, mode) {
      state.metadataMode = mode
    },
    SET_SEGMENT_MODE(state, mode) {
      state.segmentMode = mode
    },
    SET_ITEM_MODE(state, mode) {
      state.itemMode = mode
    },
    SET_TARGET_MODE(state, mode) {
      state.targetMode = mode
    },

    // active editors
    SET_SEQUENCE_ACTIVE_EDITORS(state, users) {
      state.sequenceActiveEditors = Array.isArray(users) ? [...users] : []
    },
    ADD_SEQUENCE_ACTIVE_EDITOR(state, user) {
      if (!user?.id) {
        return
      }

      const exists = state.sequenceActiveEditors.some((u) => u.id === user.id)

      if (!exists) {
        state.sequenceActiveEditors.push(user)
      }
    },
    REMOVE_SEQUENCE_ACTIVE_EDITOR(state, userId) {
      state.sequenceActiveEditors = state.sequenceActiveEditors.filter((u) => u.id !== userId)
    },

    // unsaved changes
    SET_SEQUENCE_DIRTY_CONTEXT(state, { context, dirty }) {
      state.sequenceDirtyContexts = {
        ...state.sequenceDirtyContexts,
        [context]: dirty
      }
      state.sequenceUnsavedChanges = Object.values(state.sequenceDirtyContexts).some(Boolean)
    },
    CLEAR_ALL_SEQUENCE_DIRTY_CONTEXTS(state) {
      state.sequenceDirtyContexts = {
        metadata: false,
        segment: false,
        item: false,
        target: false
      }
      state.sequenceUnsavedChanges = false
    }
  },

  actions: {
    // sequences
    async loadSequences({ commit, state }, { station, filters, sort, page, per_page } = {}) {
      commit('SET_SEQUENCE_LIST_LOADING', true)
      commit('SET_SEQUENCE_LIST_ERROR', null)
      const mergedFilters = { ...state.listFilters, ...(filters || {}) }
      const params = {
        ...Object.fromEntries(Object.entries(mergedFilters).filter(([, v]) => v !== null && v !== undefined && v !== '')),
        page: page ?? state.listPagination.current_page,
        per_page: per_page ?? state.listPagination.per_page,
        sort
      }
      try {
        const resp = await api.sequences.getSequencesList(station, params)
        const body = resp?.data || {}
        commit('SET_SEQUENCE_LIST', body.data || body.sequences || [])
        if (body.pagination) commit('SET_SEQUENCE_LIST_PAGINATION', body.pagination)
        else if (body.meta) {
          commit('SET_SEQUENCE_LIST_PAGINATION', {
            current_page: body.meta.current_page,
            per_page: body.meta.per_page,
            total: body.meta.total,
            total_pages: body.meta.last_page
          })
        }
        return body
      } catch (e) {
        commit('SET_SEQUENCE_LIST_ERROR', e)
        throw e
      } finally {
        commit('SET_SEQUENCE_LIST_LOADING', false)
      }
    },

    async loadSequence({ commit }, id) {
      commit('SET_CURRENT_SEQUENCE_LOADING', true)
      commit('SET_CURRENT_SEQUENCE_ERROR', null)
      try {
        const resp = await api.sequences.getSequence(id)
        const sequence = resp?.data?.data || resp?.data || null
        commit('SET_CURRENT_SEQUENCE', sequence)
        return sequence
      } catch (e) {
        commit('SET_CURRENT_SEQUENCE_ERROR', e)
        throw e
      } finally {
        commit('SET_CURRENT_SEQUENCE_LOADING', false)
      }
    },

    async clearSequenceCache({ commit }, id) {
      commit('SET_CURRENT_SEQUENCE_LOADING', true)
      commit('SET_CURRENT_SEQUENCE_ERROR', null)
      try {
        const resp = await api.sequences.clearSequenceCache(id)
        return resp?.data?.data || resp?.data || null
      } catch (e) {
        commit('SET_CURRENT_SEQUENCE_ERROR', e)
        throw e
      } finally {
        commit('SET_CURRENT_SEQUENCE_LOADING', false)
      }
    },

    async loadSequenceSegmentAndItemsOnly({ commit, state }, id) {
      commit('SET_CURRENT_SEQUENCE_SEGMENTS_ITEMS_LOADING', true)
      commit('SET_CURRENT_SEQUENCE_ERROR', null)
      try {
        const sequence = state.currentSequence
        const respItems = await api.sequences.getSequenceItemsList(id)
        const sequenceItems = respItems?.data?.data || respItems?.data || null
        const respSegments = await api.sequences.getSequenceSegmentsList(id)
        const sequenceSegments = respSegments?.data?.data || respSegments?.data || null
        sequence.items = sequenceItems || sequence.items
        sequence.segments = sequenceSegments || sequence.segments
        commit('SET_CURRENT_SEQUENCE', sequence)
        return sequence
      } catch (e) {
        commit('SET_CURRENT_SEQUENCE_ERROR', e)
        throw e
      } finally {
        commit('SET_CURRENT_SEQUENCE_SEGMENTS_ITEMS_LOADING', false)
      }
    },

    async loadSequenceTargetsOnly({ commit, state }, id) {
      commit('SET_CURRENT_SEQUENCE_TARGETS_LOADING', true)
      commit('SET_CURRENT_SEQUENCE_ERROR', null)
      try {
        const sequence = state.currentSequence
        const respTargets = await api.sequences.getSequenceDistributionTargetsList(id)
        const sequenceTargets = respTargets?.data?.data || respTargets?.data || null
        sequence.distribution_targets = sequenceTargets || sequence.distribution_targets
        commit('SET_CURRENT_SEQUENCE', sequence)
        return sequence
      } catch (e) {
        commit('SET_CURRENT_SEQUENCE_ERROR', e)
        throw e
      } finally {
        commit('SET_CURRENT_SEQUENCE_TARGETS_LOADING', false)
      }
    },

    async loadSequenceItem({ commit, state }, id) {
      const respItem = await api.sequences.getSequenceItem(id)
      return respItem?.data?.data || respItem?.data || null
    },

    async createSequence({ commit }, { station, payload }) {
      commit('SET_SAVING', true)
      try {
        const resp = await api.sequences.createSequence(station, payload)
        const sequence = resp?.data?.data || resp?.data
        commit('SET_CURRENT_SEQUENCE', sequence)
        return sequence
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async updateSequence({ commit, state }, { id, payload }) {
      const snapshot = state.currentSequence ? { ...state.currentSequence } : null
      commit('SET_SAVING', true)
      commit('PATCH_CURRENT_SEQUENCE', payload)
      try {
        const resp = await api.sequences.updateSequence(id, payload)
        const sequence = resp?.data?.data || resp?.data
        commit('PATCH_CURRENT_SEQUENCE', sequence)
        return sequence
      } catch (e) {
        if (snapshot) commit('SET_CURRENT_SEQUENCE', snapshot)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async updateSequenceMeta({ commit, state }, { id, payload }) {
      const snapshot = state.currentSequence ? { ...state.currentSequence } : null
      commit('SET_SAVING', true)
      const fullPayload = { ...state.currentSequence, metadata: payload }
      commit('PATCH_CURRENT_SEQUENCE', fullPayload)
      try {
        const resp = await api.sequences.updateSequence(id, {
          metadata: payload
        })
        const sequence = resp?.data?.data || resp?.data
        commit('PATCH_CURRENT_SEQUENCE', sequence)
        return sequence
      } catch (e) {
        if (snapshot) commit('SET_CURRENT_SEQUENCE', snapshot)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async duplicateSequence({ commit }, id) {
      const resp = await api.sequences.duplicateSequence(id)
      return resp?.data?.data || resp?.data
    },

    async deleteSequence({ commit, state }, id) {
      const snapshot = state.list.slice()
      commit(
        'SET_SEQUENCE_LIST',
        state.list.filter((s) => s.id !== id)
      )
      try {
        const resp = await api.sequences.removeSequence(id)
        return resp?.data
      } catch (e) {
        commit('SET_SEQUENCE_LIST', snapshot)
        throw e
      }
    },

    // items
    async addSequenceItem({ commit }, { sequenceId, payload }) {
      const tempId = `temp-${Date.now()}`
      const optimistic = { ...payload, id: tempId, tempId }
      commit('ADD_SEQUENCE_ITEM', optimistic)
      try {
        const resp = await api.sequences.addSequenceItem(sequenceId, payload)
        const saved = resp?.data?.data || resp?.data
        commit('REMOVE_SEQUENCE_ITEM', tempId)
        commit('ADD_SEQUENCE_ITEM', saved)
        return saved
      } catch (e) {
        commit('REMOVE_SEQUENCE_ITEM', tempId)
        throw e
      }
    },

    async updateSequenceItem({ commit, state }, { sequenceId, itemId, payload }) {
      const snapshot = state.currentSequence?.items?.find((x) => x.id === itemId)
      commit('UPDATE_SEQUENCE_ITEM', { id: itemId, ...payload })
      try {
        const resp = await api.sequences.updateSequenceItem(itemId, payload)
        const saved = resp?.data?.data || resp?.data
        if (saved) commit('UPDATE_SEQUENCE_ITEM', saved)
        return saved
      } catch (e) {
        if (snapshot) commit('UPDATE_SEQUENCE_ITEM', snapshot)
        throw e
      }
    },

    async removeSequenceItem({ commit, state }, { sequenceId, itemId }) {
      const snapshot = state.currentSequence?.items?.slice() || []
      commit('REMOVE_SEQUENCE_ITEM', itemId)
      try {
        const resp = await api.sequences.removeSequenceItem(itemId)
        return resp?.data
      } catch (e) {
        commit('SET_SEQUENCE_ITEMS', snapshot)
        throw e
      }
    },

    async reorderSequenceItems({ commit, state }, { orderedIds }) {
      const segmentId = 'unassigned'

      const items = state.currentSequence?.items || []

      const originalIds =
        state.dirtySegments.unassigned?.originalIds ||
        items
          .sort((a, b) => {
            return Number(a.sort_order || 999999) - Number(b.sort_order || 999999)
          })
          .map((x) => x.id)

      commit('REORDER_SEQUENCE_ITEMS', orderedIds)

      commit('SET_DIRTY_SEGMENT', {
        segmentId,
        originalIds,
        orderedIds
      })
    },

    /* async moveSequenceItem({ commit, state }, { sequenceId, itemId, segmentId, position }) {
      const snapshot = state.currentSequence?.items?.slice() || []
      commit('UPDATE_SEQUENCE_ITEM', { id: itemId, segment_id: segmentId, position })
      try {
        const resp = await api.sequences.moveSequenceItem(itemId, { sequenceId, segmentId, position })
        const items = resp?.data?.items || resp?.data?.data || null
        if (Array.isArray(items)) commit('SET_SEQUENCE_ITEMS', items)
        return resp?.data
      } catch (e) {
        commit('SET_SEQUENCE_ITEMS', snapshot)
        throw e
      }
    },*/

    async moveSequenceItem({ commit, state }, { sequenceId, itemId, segmentId, position }) {
      const snapshot = state.currentSequence?.items?.slice() || []

      const items = state.currentSequence?.items || []
      const item = items.find((x) => x.id === itemId)

      if (!item) return

      const oldSegmentId = item.segment_id ?? 'unassigned'
      const newSegmentId = segmentId ?? 'unassigned'

      /**
       * CASE 1: CROSS-SEGMENT MOVE
       * → MUST persist immediately
       */
      const isCrossSegmentMove = oldSegmentId !== newSegmentId

      if (isCrossSegmentMove) {
        // optimistic update
        commit('UPDATE_SEQUENCE_ITEM', {
          id: itemId,
          segment_id: segmentId,
          sort_order_in_segment: position
        })

        try {
          const resp = await api.sequences.moveSequenceItem(itemId, {
            sequenceId,
            segmentId,
            position
          })

          const updated = resp?.data?.items || resp?.data?.data || null

          if (Array.isArray(updated)) {
            commit('SET_SEQUENCE_ITEMS', updated)
          }

          // IMPORTANT:
          // moving between segments invalidates ordering in BOTH segments
          const affectedSegments = [oldSegmentId, newSegmentId]

          affectedSegments.forEach((segId) => {
            commit('CLEAR_DIRTY_SEGMENT', String(segId))
          })

          return resp?.data
        } catch (e) {
          commit('SET_SEQUENCE_ITEMS', snapshot)
          throw e
        }
      }

      /**
       * CASE 2: SAME SEGMENT MOVE
       * → ONLY optimistic reorder + mark dirty
       */
      commit('UPDATE_SEQUENCE_ITEM', {
        id: itemId,
        segment_id: segmentId,
        sort_order_in_segment: position
      })

      const collectSegmentIds = (segId) => {
        return (state.currentSequence?.items || [])
          .filter((x) => (x.segment_id ?? 'unassigned') === (segId ?? 'unassigned'))
          .sort((a, b) => Number(a.sort_order_in_segment ?? 999999) - Number(b.sort_order_in_segment ?? 999999))
          .map((x) => x.id)
      }

      const orderedIds = collectSegmentIds(segmentId)

      const existingDirty = state.dirtySegments[String(segmentId)] || null

      commit('SET_DIRTY_SEGMENT', {
        segmentId,
        originalIds: existingDirty?.originalIds || orderedIds,
        orderedIds
      })
    },

    async duplicateSequenceItem({ commit }, { sequenceId, itemId, segmentId, position }) {
      const resp = await api.sequences.duplicateSequenceItem(itemId, { sequenceId, segmentId, position })
      const saved = resp?.data?.data || resp?.data
      if (saved) commit('ADD_SEQUENCE_ITEM', saved)
      return saved
    },

    async overrideSequenceItem({ commit, state }, { sequenceId, itemId, payload }) {
      const snapshot = state.currentSequence?.items?.find((x) => x.id === itemId)
      commit('UPDATE_SEQUENCE_ITEM', { id: itemId, ...payload })
      try {
        const resp = await api.sequences.overrideSequenceItem(itemId, payload)
        const saved = resp?.data?.data || resp?.data
        if (saved) commit('UPDATE_SEQUENCE_ITEM', saved)
        return saved
      } catch (e) {
        if (snapshot) commit('UPDATE_SEQUENCE_ITEM', snapshot)
        throw e
      }
    },

    // segments
    async addSequenceSegment({ commit }, { sequenceId, payload }) {
      commit('SET_SAVING', true)
      const tempId = `temp-${Date.now()}`
      const optimistic = { ...payload, id: tempId, tempId }
      commit('ADD_SEQUENCE_SEGMENT', optimistic)
      try {
        const resp = await api.sequences.addSequenceSegment(sequenceId, payload)
        const saved = resp?.data?.data || resp?.data
        commit('REMOVE_SEQUENCE_SEGMENT', tempId)
        commit('ADD_SEQUENCE_SEGMENT', saved)
        return saved
      } catch (e) {
        commit('REMOVE_SEQUENCE_SEGMENT', tempId)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async updateSequenceSegment({ commit, state }, { segmentId, payload }) {
      commit('SET_SAVING', true)
      const snapshot = state.currentSequence?.segments?.find((x) => x.id === segmentId)
      commit('UPDATE_SEQUENCE_SEGMENT', { id: segmentId, ...payload })
      try {
        const resp = await api.sequences.updateSequenceSegment(segmentId, payload)
        const saved = resp?.data?.data || resp?.data
        if (saved) commit('UPDATE_SEQUENCE_SEGMENT', saved)
        return saved
      } catch (e) {
        if (snapshot) commit('UPDATE_SEQUENCE_SEGMENT', snapshot)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async removeSequenceSegment({ commit, state }, { segmentId }) {
      const snapshot = state.currentSequence?.segments?.slice() || []
      commit('REMOVE_SEQUENCE_SEGMENT', segmentId)
      try {
        const resp = await api.sequences.removeSequenceSegment(segmentId)
        return resp?.data
      } catch (e) {
        commit('SET_SEQUENCE_SEGMENTS', snapshot)
        throw e
      }
    },

    async reorderSequenceSegments({ commit, state }, { sequenceId, orderedIds }) {
      const segmentsSnapshot = state.currentSequence?.segments?.slice() || []
      const itemsSnapshot = state.currentSequence?.items?.slice() || []
      commit('SET_REORDERING', true)
      commit('REORDER_SEQUENCE_SEGMENTS', orderedIds)
      try {
        // Backend reads ordered ids from `segment_ids`
        const resp = await api.sequences.reorderSequenceSegments(sequenceId, { segment_ids: orderedIds })
        // Backend loads `segments.items` and returns the sequence body
        const body = resp?.data?.data || resp?.data || {}
        const segments = body.segments || (Array.isArray(body) ? body : null)
        if (Array.isArray(segments)) {
          commit('SET_SEQUENCE_SEGMENTS', segments)
          // If segments arrived with their items loaded, also refresh the global items array
          const itemsFromSegments = segments.flatMap((s) => (Array.isArray(s.items) ? s.items : []))
          if (itemsFromSegments.length) commit('SET_SEQUENCE_ITEMS', itemsFromSegments)
        }
        return resp?.data
      } catch (e) {
        commit('SET_SEQUENCE_SEGMENTS', segmentsSnapshot)
        commit('SET_SEQUENCE_ITEMS', itemsSnapshot)
        throw e
      } finally {
        commit('SET_REORDERING', false)
      }
    },

    async lockSequenceSegment({ commit }, { segmentId }) {
      commit('SET_LOCKING', true)

      try {
        const resp = await api.sequences.lockSequenceSegment(segmentId)

        const segment = resp?.data?.data || resp?.data

        if (segment) {
          commit('UPDATE_SEQUENCE_SEGMENT', segment)
        }

        return segment
      } finally {
        commit('SET_LOCKING', false)
      }
    },

    async unlockSequenceSegment({ commit }, { segmentId }) {
      commit('SET_LOCKING', true)

      try {
        const resp = await api.sequences.unlockSequenceSegment(segmentId)

        const segment = resp?.data?.data || resp?.data

        if (segment) {
          commit('UPDATE_SEQUENCE_SEGMENT', segment)
        }

        return segment
      } finally {
        commit('SET_LOCKING', false)
      }
    },

    async reorderSequenceSegmentItems({ commit, state }, { segmentId, orderedIds }) {
      const segmentItems = (state.currentSequence?.items || [])
        .filter((x) => x.segment_id === segmentId)
        .sort((a, b) => {
          return Number(a.sort_order_in_segment || 999999) - Number(b.sort_order_in_segment || 999999)
        })

      const originalIds = state.dirtySegments[String(segmentId)]?.originalIds || segmentItems.map((x) => x.id)

      commit('REORDER_SEQUENCE_SEGMENT_ITEMS', {
        segmentId,
        orderedIds
      })

      commit('SET_DIRTY_SEGMENT', {
        segmentId,
        originalIds,
        orderedIds
      })
    },

    async saveSegmentItems({ commit, state }, { sequenceId, segmentId }) {
      const dirty = state.dirtySegments[String(segmentId)]

      if (!dirty) return

      commit('SET_REORDERING', true)

      try {
        let resp

        if (segmentId === 'unassigned') {
          resp = await api.sequences.reorderSequenceItems(sequenceId, {
            item_ids: dirty.orderedIds
          })

          const body = resp?.data?.data || resp?.data || {}

          const items = body.items || (Array.isArray(body) ? body : null)

          if (Array.isArray(items)) {
            commit('SET_SEQUENCE_ITEMS', items)
          }
        } else {
          resp = await api.sequences.reorderSequenceSegmentItems(segmentId, {
            item_ids: dirty.orderedIds
          })

          const segment = resp?.data?.data || resp?.data

          const segmentItems = segment?.items || []

          if (Array.isArray(segmentItems)) {
            const ids = new Set(segmentItems.map((x) => x.id))

            const map = new Map(segmentItems.map((x) => [x.id, x]))

            const merged = (state.currentSequence?.items || []).map((x) => {
              if (x.segment_id !== segmentId) return x

              return map.get(x.id) || x
            })

            commit(
              'SET_SEQUENCE_ITEMS',
              merged.filter((x) => x.segment_id !== segmentId || ids.has(x.id))
            )
          }
        }

        commit('CLEAR_DIRTY_SEGMENT', segmentId)

        return resp?.data
      } finally {
        commit('SET_REORDERING', false)
      }
    },

    resetSegmentItems({ commit, state }, segmentId) {
      const dirty = state.dirtySegments[String(segmentId)]

      if (!dirty) return

      if (segmentId === 'unassigned') {
        commit('REORDER_SEQUENCE_ITEMS', dirty.originalIds)
      } else {
        commit('REORDER_SEQUENCE_SEGMENT_ITEMS', {
          segmentId,
          orderedIds: dirty.originalIds
        })
      }

      commit('CLEAR_DIRTY_SEGMENT', segmentId)
    },

    // distribution targets
    async addSequenceDistributionTarget({ commit }, { sequenceId, payload }) {
      commit('SET_SAVING', true)
      const tempId = `temp-${Date.now()}`
      const optimistic = { ...payload, id: tempId, tempId }
      commit('ADD_TARGET', optimistic)
      try {
        const resp = await api.sequences.addSequenceDistributionTarget(sequenceId, payload)
        const saved = resp?.data?.data || resp?.data
        commit('REMOVE_TARGET', tempId)
        commit('ADD_TARGET', saved)
        return saved
      } catch (e) {
        commit('REMOVE_TARGET', tempId)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async updateSequenceDistributionTarget({ commit, state }, { targetId, payload }) {
      commit('SET_SAVING', true)
      const snapshot = state.currentSequence?.targets?.find((x) => x.id === targetId)
      commit('UPDATE_TARGET', { id: targetId, ...payload })
      try {
        const resp = await api.sequences.updateSequenceDistributionTarget(targetId, payload)
        const saved = resp?.data?.data || resp?.data
        if (saved) commit('UPDATE_TARGET', saved)
        return saved
      } catch (e) {
        if (snapshot) commit('UPDATE_TARGET', snapshot)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async removeSequenceDistributionTarget({ commit, state }, { targetId }) {
      const snapshot = state.currentSequence?.targets?.slice() || []
      commit('REMOVE_TARGET', targetId)
      try {
        const resp = await api.sequences.removeSequenceDistributionTarget(targetId)
        return resp?.data
      } catch (e) {
        commit('SET_TARGETS', snapshot)
        throw e
      }
    },

    async loadSequenceTarget({ commit, state }, id) {
      const respTarget = await api.sequences.getSequenceDistributionTarget(id)
      return respTarget?.data?.data || respTarget?.data || null
    },

    // platforms
    async loadSequencePlatforms({ commit }, { station } = {}) {
      commit('SET_SEQUENCE_PLATFORMS_LOADING', true)
      try {
        const resp = await api.pcc.getSequencesPlatforms({ station })
        commit('SET_SEQUENCE_PLATFORMS', resp?.data?.data || resp?.data || [])
        return resp?.data
      } finally {
        commit('SET_SEQUENCE_PLATFORMS_LOADING', false)
      }
    },

    // formats
    async loadSequenceFormats({ commit }, { station } = {}) {
      commit('SET_FORMATS_LOADING', true)
      try {
        const resp = await api.sequences.getSequenceFormatsList({ station })
        commit('SET_FORMATS', resp?.data?.data || resp?.data || [])
        return resp?.data
      } finally {
        commit('SET_FORMATS_LOADING', false)
      }
    },

    async instantiateSequenceFromTemplate({ commit }, { formatId, payload }) {
      commit('SET_SAVING', true)
      try {
        const resp = await api.sequences.instantiateSequenceFromTemplate(formatId, payload)
        const sequence = resp?.data?.data || resp?.data
        if (sequence) commit('SET_CURRENT_SEQUENCE', sequence)
        return sequence
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async instantiateSequenceFromTemplateBulk({ commit }, { formatId, payload }) {
      commit('SET_SAVING', true)
      try {
        const resp = await api.sequences.instantiateSequenceFromTemplateBulk(formatId, payload)
        const body = resp?.data?.data || resp?.data || {}
        return Array.isArray(body) ? body : body.sequences || body.data || []
      } finally {
        commit('SET_SAVING', false)
      }
    },

    // timing & lock & versions
    async lockSequence({ commit }, sequenceId) {
      commit('SET_LOCKING', true)
      try {
        const resp = await api.sequences.lockSequence(sequenceId)
        const sequence = resp?.data?.data || resp?.data
        // if (sequence) commit('PATCH_CURRENT_SEQUENCE', sequence)
        return sequence
      } finally {
        commit('SET_LOCKING', false)
      }
    },

    async unlockSequence({ commit }, sequenceId) {
      commit('SET_LOCKING', true)
      try {
        const resp = await api.sequences.unlockSequence(sequenceId)
        const sequence = resp?.data?.data || resp?.data
        // if (sequence) commit('PATCH_CURRENT_SEQUENCE', sequence)
        return sequence
      } finally {
        commit('SET_LOCKING', false)
      }
    },

    async getSequenceTiming({ commit }, sequenceId) {
      const resp = await api.sequences.getSequenceTiming(sequenceId)
      const timing = resp?.data?.data || resp?.data
      commit('PATCH_CURRENT_SEQUENCE', { timing })
      return timing
    },

    async recalculateSequenceTiming({ commit }, sequenceId) {
      commit('SET_RECALCULATING', true)
      try {
        const resp = await api.sequences.recalculateSequenceTiming(sequenceId)
        const timing = resp?.data?.data || resp?.data
        commit('PATCH_CURRENT_SEQUENCE', { timing })
        return timing
      } finally {
        commit('SET_RECALCULATING', false)
      }
    },

    async createSequenceVersion({ commit }, { id, payload = {} }) {
      const resp = await api.sequences.createSequenceVersion(id, payload)
      return resp?.data?.data || resp?.data
    },

    async getSequenceChanges({ commit }, { sequenceId, params }) {
      const resp = await api.sequences.getSequenceChanges(sequenceId, params)
      return resp?.data
    },

    async getSequenceVersions({ commit }, sequenceId) {
      const resp = await api.sequences.getSequenceVersions(sequenceId)
      return resp?.data?.data || resp?.data
    },

    // readiness
    async getSequenceReadiness({ commit }, sequenceId) {
      commit('SET_READINESS_LOADING', true)
      try {
        const resp = await api.sequences.getSequenceReadiness(sequenceId)
        const readiness = resp?.data?.data || resp?.data
        commit('SET_READINESS', readiness)
        return readiness
      } finally {
        commit('SET_READINESS_LOADING', false)
      }
    },

    async loadSequencesListReadiness({ commit }, { station, ids } = {}) {
      if (!Array.isArray(ids) || !ids.length) {
        commit('SET_LIST_READINESS', [])
        return []
      }
      commit('SET_LIST_READINESS_LOADING', true)
      try {
        const resp = await api.sequences.getSequencesListReadiness(station, { ids })
        const data = resp?.data?.data || resp?.data || []
        const list = Array.isArray(data) ? data : []
        commit('SET_LIST_READINESS', list)
        return list
      } catch (e) {
        commit('SET_LIST_READINESS', [])
        throw e
      } finally {
        commit('SET_LIST_READINESS_LOADING', false)
      }
    },

    // export
    async exportSequenceToTarget({ commit }, { sequenceId, targetId }) {
      commit('SET_EXPORTING', true)
      try {
        const resp = await api.sequences.exportSequenceToTarget(sequenceId, targetId)
        return resp?.data
      } finally {
        commit('SET_EXPORTING', false)
      }
    },

    async exportSequenceAll({ commit }, sequenceId) {
      commit('SET_EXPORTING', true)
      try {
        const resp = await api.sequences.exportSequenceAll(sequenceId)
        return resp?.data
      } finally {
        commit('SET_EXPORTING', false)
      }
    },

    // reorder
    async saveAllDirtySegments({ dispatch, state, commit }, { sequenceId }) {
      const dirtySegments = state.dirtySegments || {}

      const segmentIds = Object.keys(dirtySegments)

      if (!segmentIds.length) return

      commit('SET_REORDERING', true)

      try {
        for (const segmentId of segmentIds) {
          await dispatch('saveSegmentItems', {
            sequenceId,
            segmentId
          })
        }

        // optional safety cleanup
        commit('CLEAR_ALL_DIRTY_SEGMENTS')

        return true
      } catch (e) {
        throw e
      } finally {
        commit('SET_REORDERING', false)
      }
    },
    resetAllDirtySegments({ commit, state }) {
      const dirtySegments = state.dirtySegments || {}

      Object.entries(dirtySegments).forEach(([segmentId, dirty]) => {
        if (!dirty) return

        if (segmentId === 'unassigned') {
          commit('REORDER_SEQUENCE_ITEMS', dirty.originalIds)
        } else {
          commit('REORDER_SEQUENCE_SEGMENT_ITEMS', {
            segmentId,
            orderedIds: dirty.originalIds
          })
        }
      })

      commit('CLEAR_ALL_DIRTY_SEGMENTS')
    },
    clearAllDirtySegments({ commit }) {
      commit('CLEAR_ALL_DIRTY_SEGMENTS')
    },

    // grid
    triggerGridRefresh({ commit }) {
      commit('INCREMENT_GRID_REFRESH_KEY')
    },

    // view/edit modes
    setMetadataMode({ commit }, mode) {
      commit('SET_METADATA_MODE', mode)
    },
    setSegmentMode({ commit }, mode) {
      commit('SET_SEGMENT_MODE', mode)
    },
    setItemMode({ commit }, mode) {
      commit('SET_ITEM_MODE', mode)
    },
    setTargetMode({ commit }, mode) {
      commit('SET_TARGET_MODE', mode)
    },

    // active editors
    setSequenceActiveEditors({ commit }, editors) {
      commit('SET_SEQUENCE_ACTIVE_EDITORS', editors)
    },

    // unsaved changes
    markSequenceContextDirty({ commit }, context) {
      commit('SET_SEQUENCE_DIRTY_CONTEXT', {
        context,
        dirty: true
      })
    },
    clearSequenceContextDirty({ commit }, context) {
      commit('SET_SEQUENCE_DIRTY_CONTEXT', {
        context,
        dirty: false
      })
    },
    clearAllSequenceContextDirty({ commit }) {
      commit('CLEAR_ALL_SEQUENCE_DIRTY_CONTEXTS')
    }
  },

  getters: {
    sequences(state) {
      return state.list
    },
    sequenceListPagination(state) {
      return state.listPagination
    },
    sequenceListLoading(state) {
      return state.listLoading
    },
    sequenceListError(state) {
      return state.listError
    },
    sequenceListFilters(state) {
      return state.listFilters
    },

    currentSequence(state) {
      return state.currentSequence
    },
    currentSequenceLoading(state) {
      return state.currentSequenceLoading
    },
    currentSequenceSegmentsItemsLoading(state) {
      return state.currentSequenceSegmentsItemsLoading
    },
    currentSequenceTargetsLoading(state) {
      return state.currentSequenceTargetsLoading
    },
    currentSequenceError(state) {
      return state.currentSequenceError
    },
    currentSequenceItems(state) {
      return state.currentSequence?.items || []
    },
    currentSequenceSegments(state) {
      return state.currentSequence?.segments || []
    },
    currentSequenceTargets(state) {
      return state.currentSequence?.distribution_targets || []
    },

    sequencePlatforms(state) {
      return state.platforms
    },
    sequenceFormats(state) {
      return state.formats
    },
    sequenceReadiness(state) {
      return state.readiness
    },
    sequenceReadinessSequence(state) {
      return state.readiness?.sequence || null
    },
    sequenceTargetsReadiness(state) {
      return state.readiness?.targets || []
    },
    sequenceSegmentsReadiness(state) {
      return state.readiness?.segments || []
    },
    sequenceItemsReadiness(state) {
      return state.readiness?.items || []
    },
    getSequenceTargetReadinessById: (state) => (targetId) => {
      return (state.readiness?.targets || []).find((t) => t?.target_id === targetId) || null
    },
    getSequenceSegmentReadinessById: (state) => (segmentId) => {
      return (state.readiness?.segments || []).find((s) => s?.segment_id === segmentId) || null
    },
    getSequenceItemReadinessById: (state) => (itemId) => {
      return (state.readiness?.items || []).find((i) => i?.item_id === itemId) || null
    },
    getSegmentTargetsReadiness: (state) => (segmentId) => {
      const segment = (state.readiness?.segments || []).find((s) => s?.segment_id === segmentId)

      return segment?.targets || []
    },
    getItemTargetsReadiness: (state) => (itemId) => {
      const item = (state.readiness?.items || []).find((i) => i?.item_id === itemId)

      return item?.targets || []
    },
    sequenceReadinessSummary(state) {
      const s = state.readiness?.sequence

      if (!s) return null

      return {
        ready: s.ready,
        ready_items: s.ready_items,
        total_items: s.total_items,
        blocked_items: s.blocked_items
      }
    },
    sequenceListReadiness(state) {
      return state.listReadiness
    },
    sequenceListReadinessLoading(state) {
      return state.listReadinessLoading
    },
    getSequenceReadinessById: (state) => (id) => {
      const entry = (state.listReadiness || []).find((e) => e?.id === id)
      return entry ? entry.readiness?.targets : null
    },

    itemsBySegment(state) {
      const items = state.currentSequence?.items || []
      return items.reduce((acc, item) => {
        const key = item.segment_id ?? '__unassigned__'
        if (!acc[key]) acc[key] = []
        acc[key].push(item)
        return acc
      }, {})
    },
    unassignedItems(state) {
      return (state.currentSequence?.items || []).filter((i) => !i.segment_id)
    },

    sequenceLoadingState(state) {
      return {
        list: state.listLoading,
        active: state.currentSequenceLoading,
        activeSegmentsItems: state.currentSequenceSegmentsItemsLoading,
        activeTargets: state.currentSequenceTargetsLoading,
        formats: state.formatsLoading,
        platforms: state.platformsLoading,
        readiness: state.readinessLoading,
        saving: state.saving,
        reordering: state.reordering,
        exporting: state.exporting,
        locking: state.locking,
        recalculating: state.recalculating
      }
    },

    // reorder
    dirtySegments(state) {
      return state.dirtySegments
    },

    hasDirtySegments(state) {
      return Object.keys(state.dirtySegments).length > 0
    },

    isSegmentDirty: (state) => (segmentId) => {
      return !!state.dirtySegments[String(segmentId)]
    },

    // grid
    gridRefreshKey(state) {
      return state.gridRefreshKey
    },

    // view/edit modes
    getMetadataMode(state) {
      return state.metadataMode
    },
    getSegmentMode(state) {
      return state.segmentMode
    },
    getItemMode(state) {
      return state.itemMode
    },
    getTargetMode(state) {
      return state.targetMode
    },

    // active editors
    sequenceActiveEditors(state) {
      return state.sequenceActiveEditors || []
    },

    // lock/unlock
    isSequenceLocked(state) {
      return !!state.currentSequence?.is_locked
    },
    sequenceLockOwner(state) {
      return {
        id: state.currentSequence?.locked_by_user_id ?? null,
        name: state.currentSequence?.locked_by_user_name ?? null
      }
    },
    isSequenceLockedByMe(state, getters, rootState) {
      if (!getters.isSequenceLocked) {
        return false
      }
      return getters.sequenceLockOwner.id === rootState?.user?.profile?.user_id
    },
    isSequenceLockedByAnotherUser(state, getters) {
      return getters.isSequenceLocked && !getters.isSequenceLockedByMe
    },
    canEditSequence(state, getters, rootState, rootGetters) {
      if (!getters.isSequenceLocked) {
        return true
      }
      if (rootGetters.isStagehandUser) {
        return true
      }
      return getters.isSequenceLockedByMe
    },
    canLockSequence(state, getters, rootState, rootGetters) {
      if (!getters.isSequenceLocked) {
        return true
      }
      if (rootGetters.isStagehandUser) {
        return true
      }
      return getters.isSequenceLockedByMe
    },
    canUnlockSequence(state, getters, rootState, rootGetters) {
      return rootGetters.isStagehandUser || state.currentSequence.locked_by_user_id === rootState?.user?.profile?.user_id
    },
    isSequenceReadOnly(state, getters) {
      return !getters.canEditSequence
    },
    canEditSegment: (state, getters, rootState, rootGetters) => (segment) => {
      if (getters.isSequenceReadOnly) {
        return false
      }

      if (!segment?.is_locked) {
        return true
      }

      if (rootGetters.isStagehandUser) {
        return true
      }

      return segment.locked_by_user_id === rootState?.user?.profile?.user_id
    },
    canEditItem: (state, getters) => (item) => {
      if (item.segment) {
        return getters.canEditSegment(item.segment) && getters.canEditSequence
      }
      return getters.canEditSequence
    },
    canEditTarget(state, getters) {
      return getters.canEditSequence
    },
    isSequenceSegmentLocked: (state) => (segment) => {
      return !!segment?.is_locked
    },
    sequenceSegmentLockOwner: (state, getters) => (segment) => {
      if (segment?.id && !segment.locked_by_user_id) {
        const segmentId = segment.id
        segment = getters.currentSequenceSegments.find((s) => s.id === segmentId)
      }
      return {
        id: segment?.locked_by_user_id ?? null,
        name: segment?.locked_by_user_name ?? null
      }
    },
    isSequenceSegmentLockedByMe: (state, getters, rootState) => (segment) => {
      if (!getters.isSequenceSegmentLocked(segment)) {
        return false
      }
      return getters.sequenceSegmentLockOwner(segment)?.id === rootState?.user?.profile?.user_id
    },
    isSequenceSegmentLockedByAnotherUser: (state, getters) => (segment) => {
      return getters.isSequenceSegmentLocked(segment) && !getters.isSequenceSegmentLockedByMe(segment)
    },

    // unsaved changes
    hasSequenceUnsavedChanges(state) {
      return state.sequenceUnsavedChanges
    },
    dirtySequenceContexts(state) {
      return state.sequenceDirtyContexts
    },
    isSequenceMetadataDirtyContext(state) {
      return state.sequenceDirtyContexts.metadata
    },
    isSequenceSegmentDirtyContext(state) {
      return state.sequenceDirtyContexts.segment
    },
    isSequenceItemDirtyContext(state) {
      return state.sequenceDirtyContexts.item
    },
    isSequenceTargetDirtyContext(state) {
      return state.sequenceDirtyContexts.target
    }
  }
}
